import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  min-height: 100vh;
  background: var(--color-light-bg);
  color: var(--color-dark);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
`;

const CancelCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  color: #2c3e50;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const CancelIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  filter: drop-shadow(0 4px 8px rgba(255, 107, 107, 0.3));
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--color-dark);
  font-family: 'Fredoka', Arial, sans-serif;
  font-weight: 700;
  text-transform: uppercase;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #7f8c8d;
  margin-bottom: 25px;
  line-height: 1.5;
`;

const Message = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  max-width: 600px;
  line-height: 1.6;
  color: var(--color-dark);
`;

const IconContainer = styled.div`
  background: var(--color-primary);
  color: var(--color-light-bg);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  font-size: 2rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled(motion.button)`
  padding: 15px 30px;
  font-size: 1.1rem;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  background: var(--color-secondary);
  color: var(--color-light-bg);
  margin: 0.5rem;
  min-width: 200px;
  transition: all 0.3s ease;
  font-family: 'Fredoka', Arial, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(77, 186, 191, 0.3);
    background: var(--color-accent);
  }
`;

const FeatureList = styled.div`
  text-align: left;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  
  h4 {
    margin-bottom: 15px;
    color: #2c3e50;
    font-weight: 600;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  li {
    padding: 5px 0;
    display: flex;
    align-items: center;
    gap: 10px;
    
    &:before {
      content: "✨";
      font-size: 1rem;
    }
  }
`;

const InfoBox = styled.div`
  background: var(--color-secondary);
  color: var(--color-light-bg);
  padding: 1.5rem;
  border-radius: 18px;
  margin: 1rem 0;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(77, 186, 191, 0.2);
`;

const WarningBox = styled.div`
  background: var(--color-accent);
  color: var(--color-dark);
  padding: 1.5rem;
  border-radius: 18px;
  margin: 1rem 0;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(246, 166, 77, 0.2);
  font-weight: 600;
`;

const PaymentCancel = () => {
  const navigate = useNavigate();

  const handleTryAgain = () => {
    navigate('/payment');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <Container>
      <CancelCard
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <CancelIcon>😔</CancelIcon>
        
        <Title>Payment Cancelled</Title>
        
        <Subtitle>
          No worries! Your payment was cancelled and no charges were made.
          You can try again anytime you're ready.
        </Subtitle>

        <Message>
          💡 <strong>Don't miss out!</strong> Premium features are waiting for you.
          Your cart items are still saved and ready for checkout.
        </Message>

        <FeatureList>
          <h4>🚀 What you're missing:</h4>
          <ul>
            <li>Unlimited wheel segments</li>
            <li>Premium themes & customization</li>
            <li>Private password-protected rooms</li>
            <li>Detailed game analytics</li>
            <li>Custom branding & logos</li>
            <li>Priority support</li>
            <li>Ad-free experience</li>
          </ul>
        </FeatureList>

        <ActionButtons>
          <Button
            className="primary"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleTryAgain}
          >
            Try Again
          </Button>
          
          <Button
            className="secondary"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleGoHome}
          >
            Back to Game
          </Button>
        </ActionButtons>

        <div style={{
          marginTop: '30px',
          padding: '15px',
          fontSize: '0.9rem',
          color: '#7f8c8d',
          borderTop: '1px solid rgba(0, 0, 0, 0.1)'
        }}>
          🔒 All payments are securely processed by Stripe<br/>
          💳 We accept all major credit cards and PayPal
        </div>
      </CancelCard>
    </Container>
  );
};

export default PaymentCancel; 